import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Textarea,
} from '@zond/ui';
import axios, { AxiosError } from 'axios';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

const referenceFormSchema = z.object({
  workEthic: z.string().optional(),
  cooperation: z.string().optional(),
  adaptabilityToStaff: z.string().optional(),
  adaptabilityToUnit: z.string().optional(),
  attendance: z.string().optional(),
  professionalism: z.string().optional(),
  eligibleForRehire: z.boolean().optional(),
  overallAttitude: z.string().optional(),
  additionalComments: z.string().optional(),
});

type ReferenceFormData = z.infer<typeof referenceFormSchema>;

export default function ReferenceFormPage() {
  const { t } = useTranslation('reference');
  const { token } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const form = useForm<ReferenceFormData>({
    resolver: zodResolver(referenceFormSchema),
    defaultValues: {
      workEthic: '',
      cooperation: '',
      adaptabilityToStaff: '',
      adaptabilityToUnit: '',
      attendance: '',
      professionalism: '',
      eligibleForRehire: false,
      overallAttitude: '',
      additionalComments: '',
    },
  });

  const onSubmit = async (data: ReferenceFormData) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      await axios.post(`${import.meta.env.VITE_API_URL}/api/references/response/${token}`, data);

      setSubmitSuccess(true);
    } catch (error) {
      const data = (error as AxiosError)?.response?.data as {
        message: string;
      };
      const responseMessage = data?.message;
      setSubmitError(responseMessage ?? t('form.submitError'));
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 bg-green-50 rounded-lg">
        <h2 className="text-2xl font-bold text-green-700 mb-4">{t('success.title')}</h2>
        <p className="text-green-600">{t('success.description')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto mt-8 p-6 bg-white rounded-lg shadow">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">{t('form.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('form.description')}</p>
        </div>

        {submitError && (
          <div className="p-4 bg-destructive/10 text-destructive rounded-md">{submitError}</div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="workEthic"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.workEthic.label')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('fields.workEthic.placeholder')}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cooperation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.cooperation.label')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('fields.cooperation.placeholder')}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="adaptabilityToStaff"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.adaptabilityToStaff.label')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('fields.adaptabilityToStaff.placeholder')}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="adaptabilityToUnit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.adaptabilityToUnit.label')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('fields.adaptabilityToUnit.placeholder')}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="attendance"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.attendance.label')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('fields.attendance.placeholder')}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="professionalism"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.professionalism.label')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('fields.professionalism.placeholder')}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="overallAttitude"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.overallAttitude.label')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('fields.overallAttitude.placeholder')}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="eligibleForRehire"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>{t('fields.eligibleForRehire.label')}</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="additionalComments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.additionalComments.label')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('fields.additionalComments.placeholder')}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t('form.submitting') : t('form.submit')}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}
